/**
 * Role Editor (ORCH-333, ORCH-21 §11)
 *
 * Purpose: The full-page editor for one role: its name and description, and
 *          one grouped table of everything it grants, organised by Area
 *          (ORCH-415). New roles go to the active tenant.
 * Rules: what the editor derives as you go (parents, dependencies, included
 *        permissions, "scope needed") lives in roleDraft.ts and matches
 *        RoleService; the server re-checks every save and its refusals are
 *        shown against the row they name. System roles, GLOBAL roles outside
 *        GLOBAL and users without role.edit / role.create get a read-only view.
 * Routes: /admin/role-manager/new and /admin/role-manager/:roleId
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Home as HomeIcon,
  SecurityOutlined as RoleIcon,
} from '@mui/icons-material';

import SkeletonPage from '../../../components/layout/SkeletonPage';
import {
  DescriptionField,
  LoadingButton,
  NotificationSnackbar,
  SecondaryButton,
} from '../../../components/common';
import { useAuth } from '../../../context/AuthContext';
import {
  useNavigationGuard,
  useUnsavedChangesGuard,
} from '../../../context/NavigationGuardContext';
import { ModuleKey, PermissionKey } from '../../../constants/permissionKeys';
import { authzService, roleErrorMessage } from '../../../services/authzService';
import { FieldDefinition, metadataService } from '../../../services/metadataService';
import {
  Dimension,
  ModuleLevel,
  PermissionCatalog,
  Role,
  RoleAdjustment,
  RoleErrorDetail,
} from '../../../types/authz';
import { formatDateTime } from '../../../utils/datetime';
import { activeTenantFeatures } from '../../../utils/moduleAccess';
import { formatAuditUser } from '../../../utils/formatting';
import DuplicateRoleDialog from './DuplicateRoleDialog';
import PermissionTable from './PermissionTable';
import { describeAdjustments, isRoleWritable } from './RoleManagerPage';
import RolePeopleSection from './RolePeopleSection';
import {
  RoleDraft,
  draftFromRole,
  emptyDraft,
  fromModules,
  scopeNeeded,
  toWrite,
  warnings,
} from './roleDraft';

const GLOBAL = 'GLOBAL';

/** A draft as text with sorted keys, so editing a cell and putting it back reads as unchanged. */
const snapshot = (draft: RoleDraft): string =>
  JSON.stringify(draft, (_, value) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
      : value
  );

const Section: React.FC<{ title: string; hint: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Box>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary">
        {hint}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const RoleEditorPage: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, can, hasModule } = useAuth();
  const { confirmNavigation } = useNavigationGuard();
  const tenantId = state.user?.activeTenant?.tenant_id ?? '';
  const inGlobal = tenantId === GLOBAL;
  const isNew = !roleId;

  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [dimensions, setDimensions] = useState<Dimension[]>([]);
  const [fieldDefinitions, setFieldDefinitions] = useState<Record<string, FieldDefinition>>({});
  const [role, setRole] = useState<Role | null>(null);
  const [draft, setDraft] = useState<RoleDraft>(emptyDraft());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<{ message: string; key?: string } | null>(null);
  const [saved, setSaved] = useState<{ message: string; severity: 'success' | 'info' } | null>(
    null
  );
  const [pristine, setPristine] = useState<string>('');
  const [failedPickers, setFailedPickers] = useState<Set<string>>(new Set());
  // What the table did for the user (auto-ticked parents, keys added at Inherit)
  const [tableNotice, setTableNotice] = useState<string | null>(null);
  const [duplicating, setDuplicating] = useState(false);

  // A notice handed over by whatever navigated here (create, duplicate). Read on every
  // navigation, not just on mount: /new and /:roleId share one editor instance. Then
  // cleared, so a reload doesn't show it again.
  useEffect(() => {
    const opened = location.state as { notice?: string; severity?: 'success' | 'info' } | null;
    if (!opened?.notice) return;
    setSaved({ message: opened.notice, severity: opened.severity ?? 'success' });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.key, location.state, location.pathname, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [permissionCatalog, dimensionList, existing] = await Promise.all([
        authzService.getCatalog(),
        authzService.getDimensions(),
        roleId ? authzService.getRole(roleId) : Promise.resolve(null),
      ]);
      setCatalog(permissionCatalog);
      setDimensions(dimensionList);
      setRole(existing);
      const initial = existing ? draftFromRole(existing) : emptyDraft();
      setDraft(initial);
      setPristine(snapshot(initial));
      const pickers = await Promise.all(
        dimensionList.map((d) =>
          metadataService
            .getFieldDefinition(d.picker_fielddef_id, tenantId)
            .then((fd): [string, FieldDefinition] => [d.key, fd])
            .catch(() => null)
        )
      );
      setFieldDefinitions(
        Object.fromEntries(pickers.filter((p): p is [string, FieldDefinition] => Boolean(p)))
      );
      setFailedPickers(new Set(dimensionList.filter((_, i) => !pickers[i]).map((d) => d.key)));
    } catch (e) {
      setLoadError(roleErrorMessage(e, 'Failed to load the role'));
    } finally {
      setLoading(false);
    }
  }, [roleId, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const readOnly = isNew
    ? !can(PermissionKey.ROLE_CREATE)
    : !role || !isRoleWritable(role) || !can(PermissionKey.ROLE_EDIT);
  // A standard role is copied into the active tenant; nothing is authored in GLOBAL (ORCH-415)
  const canDuplicate =
    Boolean(role) && role?.tenant_id === GLOBAL && can(PermissionKey.ROLE_CREATE) && !inGlobal;

  const labels = useMemo(
    () =>
      Object.fromEntries(
        [...(catalog?.permissions ?? []), ...(catalog?.modules ?? [])].map((d) => [d.key, d.label])
      ),
    [catalog]
  );
  const fromModule = useMemo(
    () => (catalog ? fromModules(draft, catalog) : new Set<string>()),
    [draft, catalog]
  );
  const needed = useMemo(
    () => new Set(catalog ? scopeNeeded(draft, catalog, dimensions) : []),
    [draft, catalog, dimensions]
  );
  const notices = useMemo(() => warnings(draft, labels), [draft, labels]);

  // Decision #62: the editor grants only what they hold; the rest can stay or go, unchanged
  const holds = useCallback((key: string) => can(key as PermissionKey), [can]);
  const holdsLevel = useCallback(
    (key: string, level: ModuleLevel) => hasModule(key as ModuleKey, level),
    [hasModule]
  );
  // A GLOBAL role's levels are judged in GLOBAL, whoever is looking
  const roleInGlobal = (role?.tenant_id ?? tenantId) === GLOBAL;
  // Whose areas are available is the active tenant's licence, not the role's (§3.12 rule 2)
  const featureToggles = useMemo(() => activeTenantFeatures(state.user), [state.user]);
  const dirty = snapshot(draft) !== pristine;

  // The shared leave-without-saving guard: Back to roles, the sidebar, the
  // breadcrumbs and closing the tab all ask while the draft is dirty (ORCH-393)
  useUnsavedChangesGuard({
    // An editor still loading starts from an empty draft, which isn't unsaved work
    when: dirty && !loading,
    message: 'Your changes to this role will be lost.',
    onDiscard: () => setDraft(role ? draftFromRole(role) : emptyDraft()),
  });

  const handleDuplicated = (copy: Role, adjusted: RoleAdjustment[]) => {
    setDuplicating(false);
    navigate(`/admin/role-manager/${copy.role_id}`, {
      state: {
        notice: adjusted.length
          ? `${copy.name} created. To fit this tenant: ${describeAdjustments(adjusted, labels)}`
          : `${copy.name} created`,
        severity: adjusted.length ? 'info' : 'success',
      },
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const body = toWrite(draft);
      if (isNew) {
        const created = await authzService.createRole(body);
        setPristine(snapshot(draft));
        navigate(`/admin/role-manager/${created.role_id}`, {
          replace: true,
          state: { notice: `${created.name} created` },
        });
      } else if (roleId) {
        const updated = await authzService.updateRole(roleId, body);
        setRole(updated);
        const next = draftFromRole(updated);
        setDraft(next);
        setPristine(snapshot(next));
        setSaved({
          message: `${updated.name} saved. People who hold it get the change on their next click.`,
          severity: 'success',
        });
      }
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: RoleErrorDetail } } })?.response?.data
        ?.detail;
      setSaveError({
        message: roleErrorMessage(e, 'The role could not be saved'),
        key: typeof detail === 'object' ? detail?.details?.key : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const title = isNew ? 'New role' : (role?.name ?? 'Role');
  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <AdminIcon fontSize="inherit" /> },
    { label: 'Role Manager', href: '/admin/role-manager', icon: <RoleIcon fontSize="inherit" /> },
    { label: title },
  ];

  if (loading) {
    return (
      <SkeletonPage pageTitle={title} breadcrumbs={breadcrumbs}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </SkeletonPage>
    );
  }

  if (loadError || !catalog) {
    return (
      <SkeletonPage pageTitle={title} breadcrumbs={breadcrumbs}>
        <Alert severity="error">{loadError ?? 'Failed to load the role'}</Alert>
      </SkeletonPage>
    );
  }

  const blocked = !draft.name.trim() || needed.size > 0;

  return (
    <SkeletonPage pageTitle={title} breadcrumbs={breadcrumbs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 4 }}>
        {readOnly && (
          <Alert
            severity="info"
            data-testid="role-read-only"
            action={
              canDuplicate ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setDuplicating(true)}
                  data-testid="role-duplicate"
                >
                  Duplicate
                </Button>
              ) : undefined
            }
          >
            {role && role.tenant_id === GLOBAL
              ? inGlobal
                ? 'Standard roles are changed by a release, not here. Duplicate one from a tenant to customise it.'
                : 'Standard roles can’t be changed. Duplicate it to make an editable copy.'
              : 'You can view this role but not change it.'}
          </Alert>
        )}

        <Section title="Role" hint="A name people will recognise when they're given it.">
          {role?.updated_at && (
            <Typography variant="caption" color="text.secondary" data-testid="role-last-changed">
              Last changed {formatDateTime(role.updated_at)} by{' '}
              {formatAuditUser(role.updated_by, {
                email: role.updated_by_user?.email,
                first_name: role.updated_by_user?.first_name ?? undefined,
                last_name: role.updated_by_user?.last_name ?? undefined,
              })}
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) 2fr' },
              alignItems: 'start',
            }}
          >
            <TextField
              label="Name"
              size="small"
              value={draft.name}
              required
              disabled={readOnly}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              inputProps={{ maxLength: 100, 'data-testid': 'role-name' }}
            />
            <DescriptionField
              label="Description"
              size="small"
              minRows={2}
              maxRows={3}
              value={draft.description}
              disabled={readOnly}
              onChange={(description) => setDraft({ ...draft, description })}
            />
          </Box>
        </Section>

        <Section
          title="Permissions"
          hint="Each Area's level fills in the permissions it needs; open an Area to add more or to narrow a scope. A scoped permission needs a cell on every dimension before the role can be saved."
        >
          {tableNotice && (
            <Alert severity="info" onClose={() => setTableNotice(null)} data-testid="table-notice">
              {tableNotice}
            </Alert>
          )}
          {notices.map((n) => (
            <Alert key={n} severity="warning">
              {n}
            </Alert>
          ))}
          {saveError && (
            <Alert severity="error" data-testid="role-save-error">
              {saveError.message}
            </Alert>
          )}
          <PermissionTable
            catalog={catalog}
            dimensions={dimensions}
            tenantId={tenantId}
            fieldDefinitions={fieldDefinitions}
            failedPickers={failedPickers}
            draft={draft}
            fromModule={fromModule}
            needed={needed}
            errorKey={saveError?.key}
            readOnly={readOnly}
            roleInGlobal={roleInGlobal}
            holds={holds}
            holdsLevel={holdsLevel}
            featureToggles={featureToggles}
            onChange={setDraft}
            onNotice={setTableNotice}
          />
        </Section>

        {role && (
          <RolePeopleSection
            roleId={role.role_id}
            assignedCount={role.assigned_user_count}
            canViewPeople={can(PermissionKey.USER_VIEW)}
            defaultExpanded={location.hash === '#people'}
          />
        )}

        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
            {needed.size > 0 && (
              <Typography variant="body2" color="warning.main" data-testid="scope-needed-summary">
                {needed.size} permission(s) still need a scope on every dimension
              </Typography>
            )}
            <SecondaryButton
              onClick={() => confirmNavigation(() => navigate('/admin/role-manager'))}
            >
              Back to roles
            </SecondaryButton>
            <LoadingButton
              variant="contained"
              loading={saving}
              disabled={blocked}
              onClick={handleSave}
              data-testid="role-save"
            >
              {isNew ? 'Create role' : 'Save'}
            </LoadingButton>
          </Box>
        )}
      </Box>

      {duplicating && role && (
        <DuplicateRoleDialog
          role={role}
          existingNames={[]}
          onClose={() => setDuplicating(false)}
          onDuplicated={handleDuplicated}
        />
      )}

      <NotificationSnackbar
        open={Boolean(saved)}
        onClose={() => setSaved(null)}
        message={saved?.message ?? ''}
        severity={saved?.severity}
      />
    </SkeletonPage>
  );
};

export default RoleEditorPage;
